"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import BottomCta from "@/components/BottomCta";
import { Settings, RefreshCw, FileCheck, BadgeCheck, Zap, Users, Shield, CheckCircle2 } from "lucide-react";

const SERVICES = [
  { icon: BadgeCheck, title: "Government Liaison", desc: "Direct submission and follow-up with Dubai, Abu Dhabi, RAK and all UAE authorities." },
  { icon: RefreshCw, title: "Renewal Support", desc: "License, visa and company renewals handled without delays or complications." },
  { icon: FileCheck, title: "Employee Approvals", desc: "Work permit and labour card processing across all UAE zones and sectors." },
  { icon: Settings, title: "Compliance Filing", desc: "Accurate document preparation and submission for regulatory requirements." },
  { icon: Shield, title: "Government Coordination", desc: "Complete liaison and follow-up ensuring timely approvals and clearances." },
  { icon: Zap, title: "Fast-Track Support", desc: "Expedited processing for urgent licensing and renewal requirements." },
];

const STEPS = [
  { num: "01", title: "Request Service", desc: "Tell us your PRO requirement — renewal, approval, or government submission." },
  { num: "02", title: "Document Preparation", desc: "We prepare and organize all necessary documentation and compliance files." },
  { num: "03", title: "Government Submission", desc: "Official submission and direct coordination with relevant UAE authorities." },
  { num: "04", title: "Approval & Completion", desc: "We follow up until completion and deliver your approved documents." },
];

const WHY_CHOOSE = [
  "Direct government liaison across all UAE jurisdictions",
  "Zero delays with priority coordination and follow-up",
  "Expert handling of all compliance and renewal requirements",
  "Transparent process with regular status updates",
  "Cost-effective and reliable PRO support",
];

export default function ProServicesPage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">

          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              PRO Services
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            PRO Services <br />
            <span className="font-serif font-normal text-[#B8893A]">for Government Compliance</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            End-to-end PRO support for licensing, employee approvals, renewals, and government submissions across all UAE jurisdictions. Zero delays, complete compliance.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
          >
            <a
              href="https://wa.me/971507987470"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Request PRO Service
            </a>
            <Link
              href="/contact"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-[#B8893A] bg-transparent border-2 border-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#B8893A]/5 hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Contact PRO Team
            </Link>
          </motion.div>
        </div>
      </section>

      {/* WHY PRO MATTERS */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-y border-gray-100">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-2 gap-12 xl:gap-24">
          <div className="flex flex-col justify-center">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why PRO Support Matters</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Government liaison that works</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              Direct coordination with UAE authorities ensures your licenses are renewed, approvals granted, and compliance maintained without delays or complications.
            </p>
          </div>
          <div className="flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-8 md:pt-0 md:pl-12 xl:pl-24">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">What You Get</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Complete compliance support</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              Expert handling of all PRO services from government liaison to renewals, approvals, and submissions — handled by professionals across all UAE jurisdictions.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">What We Offer</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Complete PRO & government services</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            {SERVICES.map((item, i) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={i}
                  className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 hover:shadow-md flex flex-col justify-between"
                >
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{item.title}</h3>
                    <p className="text-gray-500 font-light text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR PROCESS */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Our Process</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Simple. Efficient. Reliable.</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 relative">
            {STEPS.map((step, i) => (
              <div key={i} className="relative group bg-white p-8 rounded-2xl border border-gray-100/80 shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="text-6xl xl:text-7xl font-black text-gray-100 group-hover:text-[#B8893A]/10 transition-colors duration-300 mb-4">
                  {step.num}
                </div>
                <h3 className="text-lg font-bold mb-2 tracking-tight">{step.title}</h3>
                <p className="text-gray-500 font-light text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-[#FAFAFA] border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Choose Us</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">PRO support you can trust</h2>
            <p className="text-gray-500 font-light leading-relaxed">
              We provide expert government liaison and PRO services across all UAE jurisdictions. Direct coordination, zero delays, complete compliance.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {WHY_CHOOSE.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border border-gray-100 bg-white rounded-xl">
                <CheckCircle2 className="w-4 h-4 text-[#B8893A] shrink-0 mt-0.5" />
                <span className="text-sm font-semibold text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCta
        title="Let Our PRO Team Handle Your Compliance"
        subtitle="Government liaison, renewals, approvals — handled by experts across all UAE jurisdictions. Contact us for immediate assistance."
      />
    </main>
  );
}
